import { injectable, inject, singleton } from 'tsyringe';
import { GovernanceContract } from '@contracts';
import { IEventListener } from '@interfaces/listeners/IEventListener';
import { IVoteCastEvent } from '@interfaces/events/governance/IVoteCastEvent';
import { EventLog } from 'ethers';

/**
 * @title Vote Cast Event Listener
 * @notice Listens for VoteCast events from the Governance contract
 * @dev This listener monitors the governance contract for vote casting events
 */
@injectable()
@singleton()
export class VoteCastListener implements IEventListener<IVoteCastEvent> {
  private isListening = false;
  private eventFilter: any;
  private callbacks: ((event: IVoteCastEvent) => void | Promise<void>)[] = [];

  constructor(
    @inject(GovernanceContract)
    private readonly governanceContract: GovernanceContract
  ) {}

  /**
   * @notice Start listening for VoteCast events
   * @param callback Function to call when event is received
   */
  async start(callback: (event: IVoteCastEvent) => void | Promise<void>): Promise<void> {
    this.addCallback(callback);

    if (this.isListening) {
      return;
    }

    this.eventFilter = this.governanceContract.instance.filters.VoteCast();

    const handleEvent = async (
      voter: string,
      proposalId: bigint,
      support: number,
      weight: bigint,
      reason: string,
      event: EventLog
    ) => {
      const voteCastEvent: IVoteCastEvent = {
        voter: voter,
        proposalId: proposalId,
        support: support,
        weight: weight,
        reason: reason,
        blockNumber: event.blockNumber,
        transactionHash: event.transactionHash,
        logIndex: event.index
      };

      // Execute all callbacks
      for (const cb of this.callbacks) {
        try {
          await cb(voteCastEvent);
        } catch (error) {
          console.error('Error in VoteCast event callback:', error);
        }
      }
    };

    this.governanceContract.instance.on(this.eventFilter, handleEvent);
    this.isListening = true;
  }

  /**
   * @notice Add a callback to the listener
   * @param callback Function to call when event is received
   */
  addCallback(callback: (event: IVoteCastEvent) => void | Promise<void>): void {
    this.callbacks.push(callback);
  }

  /**
   * @notice Remove a callback from the listener
   * @param callback Function to remove
   */
  removeCallback(callback: (event: IVoteCastEvent) => void | Promise<void>): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }
  }

  /**
   * @notice Stop listening for events
   */
  stop(): void {
    if (!this.isListening) {
      return;
    }

    this.governanceContract.instance.removeAllListeners('VoteCast');
    this.callbacks = [];
    this.isListening = false;
  }

  /**
   * @notice Get historical VoteCast events
   * @param fromBlock Starting block number
   * @param toBlock Ending block number (optional, defaults to latest)
   */
  async getHistoricalEvents(
    fromBlock: number,
    toBlock?: number | 'latest'
  ): Promise<IVoteCastEvent[]> {
    const filter = this.governanceContract.instance.filters.VoteCast();
    const events = await this.governanceContract.instance.queryFilter(filter, fromBlock, toBlock);

    return events.map((event) => {
      if ('args' in event) {
        const [voter, proposalId, support, weight, reason] = event.args;

        return {
          voter: voter,
          proposalId: proposalId,
          support: support,
          weight: weight,
          reason: reason,
          blockNumber: event.blockNumber,
          transactionHash: event.transactionHash,
          logIndex: event.index
        };
      }
      throw new Error('Invalid event format');
    });
  }

  /**
   * @notice Check if the listener is currently active
   */
  get listening(): boolean {
    return this.isListening;
  }
}
